<template>
  <div class="ma-4 px-4">
    <div class="d-flex justify-content-between mb-2">
      <v-row dense>
        <v-col cols="12" md="2">
          <v-select
            v-model="filterType"
            :items="typeOptions"
            label="Type"
            outlined
            dense
            clearable
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            v-model="filterStatus"
            :items="statusOptions"
            label="Status"
            outlined
            dense
            clearable
            hide-details="auto"
          />
        </v-col>
      </v-row>
      <v-btn color="primary" @click="openAddModal">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </div>

    <v-sheet outlined class="mt-4">
      <v-data-table
        :headers="headers"
        :items="filteredAnnouncements"
        fixed-header
        class="secondary"
      >
        <template #[`item.type`]="{ item }">
          <v-chip
            small
            dark
            :color="item.type === 'marquee' ? 'warning' : 'info'"
          >
            {{ item.type }}
          </v-chip>
        </template>

        <template #[`item.is_active`]="{ item }">
          <v-chip small dark :color="item.is_active ? 'success' : 'error'">
            {{ item.is_active ? "Active" : "Inactive" }}
          </v-chip>
        </template>

        <template #[`item.priority`]="{ item }">
          {{ getPriorityLabel(item.priority) }}
        </template>

        <template #[`item.from_date`]="{ item }">
          {{ formatDate(item.from_date) }}
        </template>

        <template #[`item.to_date`]="{ item }">
          {{ formatDate(item.to_date) }}
        </template>

        <template #[`item.attachments`]="{ item }">
          <template v-if="item.attachments && item.attachments.length">
            <v-chip
              x-small
              class="mr-1"
              @click="downloadAttachment(item.attachments[0])"
            >
              <v-icon left x-small>mdi-paperclip</v-icon>
              <span class="text-truncate" style="max-width: 120px">
                {{ item.attachments[0].file_name }}
              </span>
            </v-chip>

            <v-menu v-if="item.attachments.length > 1" offset-y>
              <template #activator="{ on, attrs }">
                <v-chip x-small v-bind="attrs" v-on="on">
                  +{{ item.attachments.length - 1 }}
                </v-chip>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="file in item.attachments.slice(1)"
                  :key="file.id"
                  @click="downloadAttachment(file)"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-paperclip</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ file.file_name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <span v-else>-</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-icon small class="mr-2" color="accent" @click="edit(item)">
            mdi-pencil
          </v-icon>
          <v-icon small color="error" @click="confirmDelete(item.id)">
            mdi-delete
          </v-icon>
        </template>

        <template #no-data>
          <div class="text-center py-2 grey--text">No announcements found</div>
        </template>
      </v-data-table>
    </v-sheet>

    <v-dialog v-model="dialog" max-width="700" persistent scrollable>
      <announcementForm
        v-if="dialog"
        :announcement="announcement"
        :edit="editMode"
        :loading="saving"
        :type-options="typeOptions"
        :priority-options="priorityOptions"
        @closeDialog="closeModal"
        @create="createAnnouncement"
        @update="updateAnnouncement"
        @download="downloadAttachment"
      />
    </v-dialog>

    <v-overlay :value="attachmentLoading" z-index="300">
      <v-progress-circular indeterminate size="48" color="primary" />
    </v-overlay>

    <attachmentViewer
      v-if="attachmentDialog"
      :value="attachmentData"
      @closeDialog="attachmentDialog = false"
    />

    <v-dialog v-model="confirmDeleteDialog" max-width="400" persistent>
      <confirmationDialog
        v-if="confirmDeleteDialog"
        confirmationText="This action cannot be undone. Do you want to delete this announcement?"
        :loading="deleting"
        @closeDialog="confirmDeleteDialog = false"
        @saveDialog="deleteAnnouncement"
      />
    </v-dialog>
  </div>
</template>

<script>
import announcementForm from "@/components/announcement/form.vue";
import confirmationDialog from "@/components/confirmationDialog.vue";
import attachmentViewer from "@/components/attachmentViewer.vue";

export default {
  name: "AnnouncementManagement",
  components: { announcementForm, confirmationDialog, attachmentViewer },
  data() {
    return {
      announcements: [],
      announcement: {},
      dialog: false,
      editMode: false,
      confirmDeleteDialog: false,
      deleteId: null,
      attachmentDialog: false,
      attachmentData: {},
      attachmentLoading: false,
      searchQuery: "",
      filterType: null,
      filterStatus: null,
      saving: false,
      deleting: false,
      typeOptions: [
        { text: "Announcement", value: "announcement" },
        { text: "Marquee", value: "marquee" },
      ],
      priorityOptions: [
        { text: "Low", value: 1 },
        { text: "Medium", value: 2 },
        { text: "High", value: 3 },
      ],
      statusOptions: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      headers: [
        { text: "Title", value: "title", width: "20%" },
        { text: "Type", value: "type", width: "10%" },
        { text: "From Date", value: "from_date", width: "15%" },
        { text: "To Date", value: "to_date", width: "15%" },
        { text: "Priority", value: "priority", width: "8%" },
        { text: "Status", value: "is_active", width: "8%" },
        {
          text: "Attachments",
          value: "attachments",
          sortable: false,
          width: "14%",
        },
        { text: "Created By", value: "created_by", width: "12%" },
        { text: "Actions", value: "actions", sortable: false, width: "8%" },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    filteredAnnouncements() {
      let filtered = this.announcements;

      if (this.filterType) {
        filtered = filtered.filter((a) => a.type === this.filterType);
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((a) =>
          a.title?.toLowerCase().includes(query),
        );
      }

      if (this.filterStatus !== null && this.filterStatus !== undefined) {
        filtered = filtered.filter((a) => a.is_active === this.filterStatus);
      }

      return filtered;
    },
  },
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Announcements" }]);
    this.initialise();
  },
  methods: {
    initialise() {
      this.getAnnouncements();
    },
    async getAnnouncements() {
      await this.$axios
        .get("announcements")
        .then((res) => {
          this.announcements = (res.data.data || []).sort(
            (a, b) => b.id - a.id,
          );
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async createAnnouncement(data, attachments) {
      this.saving = true;
      await this.$axios
        .post("announcements", {
          announcement: { ...data, created_by: this.createdBy() },
        })
        .then(async (res) => {
          await this.syncAttachments(res.data.data.id, attachments);
          this.$store.commit("showSnackbar", {
            message: "Announcement created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async updateAnnouncement(data, attachments) {
      this.saving = true;
      await this.$axios
        .put(`announcements/${data.id}`, { announcement: data })
        .then(async () => {
          await this.syncAttachments(data.id, attachments);
          this.$store.commit("showSnackbar", {
            message: "Announcement updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async deleteAnnouncement() {
      this.deleting = true;
      await this.$axios
        .delete(`announcements/${this.deleteId}`)
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Announcement deleted successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    openAddModal() {
      this.announcement = {};
      this.editMode = false;
      this.dialog = true;
    },
    edit(item) {
      this.announcement = { ...item };
      this.editMode = true;
      this.dialog = true;
    },
    closeModal() {
      this.dialog = false;
      this.editMode = false;
      this.announcement = {};
    },
    confirmDelete(id) {
      this.deleteId = id;
      this.confirmDeleteDialog = true;
    },
    resetValues() {
      this.dialog = false;
      this.editMode = false;
      this.confirmDeleteDialog = false;
      this.announcement = {};
      this.deleteId = null;
    },
    createdBy() {
      return this.currentUser?.name || this.currentUser?.email || "";
    },

    async syncAttachments(announcementId, attachments) {
      if (!announcementId || !attachments) return;

      const { newFiles = [], removedIds = [] } = attachments;

      for (const id of removedIds) {
        await this.$axios.delete(`announcements/attachments/${id}`);
      }

      for (const file of newFiles) {
        const formData = new FormData();
        formData.append("file", file);
        await this.$axios.post(
          `announcements/${announcementId}/attachments`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      }
    },

    async downloadAttachment(file) {
      if (this.attachmentLoading) return;

      this.attachmentLoading = true;
      await this.$axios
        .get(`announcements/attachments/${file.id}/download`)
        .then((res) => {
          this.attachmentData = res.data.data;
          this.attachmentDialog = true;
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        })
        .finally(() => {
          this.attachmentLoading = false;
        });
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}-${month}-${date.getFullYear()}`;
    },
    getPriorityLabel(value) {
      return this.priorityOptions.find((p) => p.value === value)?.text || value;
    },
  },
};
</script>

<style scoped></style>
